import React, { useState } from 'react';
import { Building2, Plus, Trash2, LogOut, Loader2 } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Badge } from '@shared/components/ui/badge';
import { Avatar, AvatarFallback } from '@shared/components/ui/avatar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@shared/components/ui/select';
import {
  useOrgsQuery, useCreateOrgMutation, useOrgMembersQuery,
  useInviteMemberMutation, useUpdateMemberRoleMutation, useRemoveMemberMutation,
} from '@shared/hooks/useOrgsQuery';
import { OrgMember, OrgMemberRole } from '@shared/types';
import { cn } from '@shared/lib/utils';

interface OrgManagerProps {
  /** The signed-in user's id (passed in — this component never reads an auth context). */
  currentUserId: string;
}

const ROLE_OPTIONS: OrgMemberRole[] = ['ADMIN', 'MEMBER'];

function initials(name?: string | null) {
  return (name ?? '?').trim().charAt(0).toUpperCase() || '?';
}

function MembersPanel({ orgId, myRole, currentUserId }: { orgId: string; myRole: OrgMemberRole; currentUserId: string }) {
  const { data: members = [], isLoading } = useOrgMembersQuery(orgId);
  const invite = useInviteMemberMutation(orgId);
  const updateRole = useUpdateMemberRoleMutation(orgId);
  const removeMember = useRemoveMemberMutation(orgId);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgMemberRole>('MEMBER');

  const canInvite = myRole === 'OWNER' || myRole === 'ADMIN';
  const isOwner = myRole === 'OWNER';

  const handleInvite = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    await invite.mutateAsync({ email: trimmed, role });
    setEmail('');
  };

  return (
    <div className="mt-4 space-y-3">
      {canInvite && (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Invite by email (they need a Hintword account)"
            className="h-9 flex-1 min-w-[220px] text-sm"
            onKeyDown={e => { if (e.key === 'Enter') handleInvite(); }}
          />
          <Select value={role} onValueChange={v => setRole(v as OrgMemberRole)}>
            <SelectTrigger className="h-9 w-28 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map(r => <SelectItem key={r} value={r}>{r.charAt(0) + r.slice(1).toLowerCase()}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-9" onClick={handleInvite} disabled={!email.trim() || invite.isPending}>
            {invite.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Invite'}
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading members…
        </div>
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border">
          {members.map((m: OrgMember) => {
            const isSelf = m.user_id === currentUserId;
            const memberIsOwner = m.role === 'OWNER';
            return (
              <div key={m.member_id} className="flex items-center gap-3 px-3 py-2.5">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary/10 text-primary text-xs">
                    {initials(m.user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {m.user?.name ?? m.user?.email ?? m.user_id}
                    {isSelf && <span className="ml-1.5 text-xs text-muted-foreground">(you)</span>}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{m.user?.email}</div>
                </div>

                {/* Role: owner can change others' roles; everyone else sees a badge */}
                {isOwner && !isSelf && !memberIsOwner ? (
                  <Select
                    value={m.role}
                    onValueChange={v => updateRole.mutate({ memberId: m.member_id, role: v as OrgMemberRole })}
                  >
                    <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map(r => <SelectItem key={r} value={r}>{r.charAt(0) + r.slice(1).toLowerCase()}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant={memberIsOwner ? 'default' : 'secondary'} className="text-[10px]">
                    {m.role}
                  </Badge>
                )}

                {/* Remove: owner removes others; non-owners can leave (remove self) */}
                {!memberIsOwner && (isOwner && !isSelf ? (
                  <button
                    onClick={() => removeMember.mutate(m.member_id)}
                    className="text-muted-foreground hover:text-destructive"
                    title="Remove member"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                ) : isSelf && !isOwner ? (
                  <button
                    onClick={() => removeMember.mutate(m.member_id)}
                    className="text-muted-foreground hover:text-destructive"
                    title="Leave organization"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                ) : null)}
              </div>
            );
          })}
          {members.length === 0 && (
            <div className="px-3 py-4 text-sm text-muted-foreground">No members yet.</div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Full "My organizations" management surface: list + create, and per-org
 * member administration (invite / role / remove), mirroring backend rules.
 */
export default function OrgManager({ currentUserId }: OrgManagerProps) {
  const { data: memberships = [], isLoading } = useOrgsQuery();
  const createOrg = useCreateOrgMutation();
  const [newOrgName, setNewOrgName] = useState('');
  const [openOrgId, setOpenOrgId] = useState<string | null>(null);

  const handleCreate = async () => {
    const name = newOrgName.trim();
    if (!name) return;
    const org = await createOrg.mutateAsync({ name });
    setNewOrgName('');
    setOpenOrgId(org.org_id);
  };

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="mb-6">
        <h1 className="text-xl font-semibold">Organizations</h1>
        <p className="text-sm text-muted-foreground">
          Create an organization and invite teammates to share notes with.
        </p>
      </div>

      {/* Create */}
      <div className="mb-6 flex items-center gap-2">
        <Input
          value={newOrgName}
          onChange={e => setNewOrgName(e.target.value)}
          placeholder="New organization name…"
          className="h-9 flex-1 text-sm"
          onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
        />
        <Button size="sm" className="h-9 gap-1.5" onClick={handleCreate} disabled={!newOrgName.trim() || createOrg.isPending}>
          {createOrg.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Create
        </Button>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading organizations…
        </div>
      ) : memberships.length === 0 ? (
        <div className="flex flex-col items-center rounded-lg border border-dashed border-border py-12 text-center">
          <Building2 className="mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">You're not in any organization yet.</p>
          <p className="text-xs text-muted-foreground">Create one above to start collaborating.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {memberships.map(m => {
            const org = m.org;
            if (!org) return null;
            const isOpen = openOrgId === org.org_id;
            return (
              <div key={m.member_id} className={cn('rounded-xl border border-border p-4', isOpen && 'border-primary/40')}>
                <button
                  className="flex w-full items-center gap-3 text-left"
                  onClick={() => setOpenOrgId(isOpen ? null : org.org_id)}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{org.name}</div>
                    <div className="text-xs text-muted-foreground">Your role: {m.role.toLowerCase()}</div>
                  </div>
                  <Badge variant="secondary" className="text-[10px]">{isOpen ? 'Hide members' : 'Manage members'}</Badge>
                </button>
                {isOpen && <MembersPanel orgId={org.org_id} myRole={m.role} currentUserId={currentUserId} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
